import { ArrowRightOutlined, MenuOutlined } from "@ant-design/icons"
import { App, Button, Flex, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { nextPageUrl } from "../../../redux/shared/paged-list.ts"
import { type RootState } from "../../../store.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../utils/utils"
import {
  DO_NOT_CONTACT_REASON_KEY,
  type InactiveReasonKey
} from "../../Lead/constants/operational-states.ts"
import { confirmLeadRestore } from "../helpers/confirm-lead-restore.ts"
import { leadsSetProp } from "../redux/leads.slice.ts"
import {
  deleteInactiveLead,
  fetchInactiveCounts,
  fetchInactiveLeads,
  reactivateLead,
  transitionLeadReason
} from "../redux/leads.thunks.ts"
import type { LeadsFilters } from "../redux/leads.types.ts"
import type { LeadListItem } from "../types/types.ts"
import LeadsInactiveTable from "./components/leads-list-table/LeadsInactiveTable.tsx"

const { Title, Text } = Typography

interface LeadsInactivePageOwnProps {
  /** Mounted inside the Archive page, which supplies its own header and
   *  document title — so this one renders neither and would otherwise stack
   *  a second page header under the Archive's. */
  embedded?: boolean
}

const LeadsInactivePage = ({
  embedded = false,
  leadPermissions,
  fetching,
  result,
  saving,
  filters,
  counts,
  getLeads,
  getCounts,
  deleteLead,
  reactivate,
  transitionReason,
  setProp
}: ConnectedProps<typeof connector> & LeadsInactivePageOwnProps) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const navigate = useNavigate()

  const [ordering, setOrdering] = useState<string | null>(null)

  const { count, results } = result

  function handleGetData() {
    const serializeFilters = serializeData(filters) as LeadsFilters
    const data = { ordering, ...serializeFilters }
    // Page one, always: `page` is persisted with the filters, and a scrolled
    // list has no way back to the rows a restored page would skip.
    getLeads({ ...data, page: 1 })
    getCounts({ ...data })
  }

  const handleChangeFilter = useCallback((newData: Partial<LeadsFilters>) => {
    setProp({ key: "inactiveFilters", value: { ...newData, page: 1 } })
  }, [])

  const handleSearchChange = useCallback(
    (value?: string | null) => {
      handleChangeFilter({ q: value || null })
    },
    [handleChangeFilter]
  )

  const handleLoadMore = useCallback(() => {
    const url = nextPageUrl(result)
    if (!url) return
    // The server's own url carries the queue and every filter it was built
    // with, so nothing here has to reconstruct them.
    getLeads({
      ...extractParamsFromNextUrl<LeadsFilters>(url),
      isMore: true
    })
  }, [result?.next, result?.retryNext, getLeads])

  const handleSelectRow = useCallback(
    (data: LeadListItem, type: string, payload?: unknown) => {
      switch (type) {
        case "reactivate":
          return confirmLeadRestore({
            modal,
            t,
            lead: data,
            onConfirm: () => reactivate(data?.id)
          })
        case "delete":
          return modal.confirm({
            centered: true,
            title: t("Confirm deleting"),
            content: t(
              "Are you sure you want to delete this lead? This action can NOT be undone."
            ),
            cancelText: t("Cancel"),
            okText: t("Delete"),
            okButtonProps: { danger: true, ghost: true },
            onOk() {
              deleteLead(data?.id)
            }
          })
        case "transition": {
          const reason = (payload as { reason?: InactiveReasonKey } | undefined)
            ?.reason
          if (!reason) return

          const doTransition = () =>
            transitionReason({ id: data?.id, reason })

          // Same guard as restoring — moving a lead *into* Do Not Contact is
          // just as much a stated preference as moving one out of it.
          if (reason === DO_NOT_CONTACT_REASON_KEY) {
            return modal.confirm({
              centered: true,
              title: t("Move to Do Not Contact?"),
              content: t(
                "This will mark the lead as asking not to be contacted."
              ),
              cancelText: t("Cancel"),
              okText: t("Move"),
              okButtonProps: { danger: true },
              onOk: doTransition
            })
          }
          return doTransition()
        }
      }
    },
    [modal, t, reactivate, deleteLead, transitionReason]
  )

  useEffect(() => {
    if (!embedded) generateDocumentTitle("Inactive Leads")
    setProp({ key: "editableLead", value: null })
  }, [])

  useEffect(() => {
    handleGetData()
  }, [ordering, filters])

  return (
    <div className={"grid"}>
      <Flex vertical className={"min-w-0"}>
        {!embedded && (
          <Flex justify={"space-between"} align={"center"}>
            <Flex vertical>
              <Title className={"text-start"} level={2}>
                {t("Inactive Leads")}
              </Title>
              <Text type={"secondary"}>
                {t("Leads paused, nurtured or marked do not contact.")}
              </Text>
            </Flex>

            {leadPermissions?.leads?.lead_view && (
              <Button
                type="primary"
                onClick={() => navigate(RoutesPaths.lead.list)}
                icon={<MenuOutlined />}
              >
                {t("Active Leads")}
                <ArrowRightOutlined />
              </Button>
            )}
          </Flex>
        )}

        <LeadsInactiveTable
          leads={results}
          count={count}
          counts={counts}
          canSeeWon={leadPermissions?.features?.lead_view_archive}
          loading={fetching || saving}
          filters={filters}
          ordering={ordering}
          deleteAllowed={leadPermissions?.leads?.lead_delete}
          editAllowed={leadPermissions?.leads?.lead_edit}
          next={result?.next}
          retryNext={result?.retryNext}
          onLoadMore={handleLoadMore}
          onOrderingChange={setOrdering}
          onSelectRow={handleSelectRow}
          onSearchChange={handleSearchChange}
          onFiltersChange={handleChangeFilter}
        />
      </Flex>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.leads.fetching,
  saving: state.leads.saveFetching,
  result: state.leads.inactiveResult,
  filters: state.leads.inactiveFilters,
  counts: state.leads.inactiveCounts,
  leadPermissions: state?.auth?.permissions?.codes?.leads
})

const mapDispatchToProps = {
  getLeads: fetchInactiveLeads,
  getCounts: fetchInactiveCounts,
  deleteLead: deleteInactiveLead,
  reactivate: reactivateLead,
  transitionReason: transitionLeadReason,
  setProp: leadsSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(LeadsInactivePage)
